<template lang="pug">
  div
    .mb-6
      h1.display-1.mb-2 Radio Component
      p.text--secondary Radio buttons allow users to select one option from a set of mutually exclusive options.

    // Radio Variations
    .component-section.mb-8#basic-variations
      h3.mb-4.cursor-pointer(@click="navigateToSection('basic-variations')") Basic Variations
      .variations-grid
        .variation-item.mb-6(v-for="variation in radioVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          // Grouped radios (side by side or vertical)
          template(v-if="variation.isGroup")
            .variation-preview.mb-4.pa-4.border.rounded
              // Special handling for layout variations
              template(v-if="variation.id === 'layout-variations'")
                div(v-for="(radio, index) in variation.radios" :key="`${variation.id}-radio-${index}`" class="mb-4")
                  h5.mb-2 {{ radio.label }}
                  .mb-2
                    small.text--secondary Selected: {{ JSON.stringify($data[radio.model]) }}
                  // Vertical default
                  yz-radio-group(v-if="radio.layoutType === 'vertical-default'" v-model="$data[radio.model]")
                    yz-radio(value="layout-a" label="Layout A")
                    yz-radio(value="layout-b" label="Layout B")
                    yz-radio(value="layout-c" label="Layout C")
                  // Vertical with 32px spacing
                  yz-radio-group(v-else-if="radio.layoutType === 'vertical-32'" v-model="$data[radio.model]" spacing="32px")
                    yz-radio(value="layout-a" label="Layout A")
                    yz-radio(value="layout-b" label="Layout B")
                    yz-radio(value="layout-c" label="Layout C")
                  // Horizontal default
                  yz-radio-group(v-else-if="radio.layoutType === 'horizontal-default'" v-model="$data[radio.model]" direction="horizontal")
                    yz-radio(value="layout-a" label="Layout A")
                    yz-radio(value="layout-b" label="Layout B")
                    yz-radio(value="layout-c" label="Layout C")
                  // Horizontal with 32px spacing
                  yz-radio-group(v-else-if="radio.layoutType === 'horizontal-32'" v-model="$data[radio.model]" direction="horizontal" spacing="32px")
                    yz-radio(value="layout-a" label="Layout A")
                    yz-radio(value="layout-b" label="Layout B")
                    yz-radio(value="layout-c" label="Layout C")
              // Special handling for label slot examples
              template(v-else-if="variation.id === 'label-slot'")
                .mb-3
                  strong Selected: {{ JSON.stringify($data[variation.radios[0].model]) }}
                yz-radio-group(v-model="$data[variation.radios[0].model]")
                  template(v-for="radio in variation.radios")
                    // Regular label prop
                    yz-radio(v-if="!radio.isSlot" :key="radio.value" :value="radio.value" :label="radio.label" v-bind="radio.props")
                    // Slot with HTML content
                    yz-radio(v-else-if="radio.isSlot && !radio.isRich" :key="radio.value" :value="radio.value" v-bind="radio.props")
                      template(#label)
                        span(v-html="'Custom slot with <strong class=\"text-danger-500\">HTML content</strong>'")
                    // Rich content slot
                    yz-radio(v-else-if="radio.isRich" :key="radio.value" :value="radio.value" v-bind="radio.props")
                      template(#label)
                        .d-flex.align-center
                          v-icon.mr-2(small color="info500") mdi-star
                          span Premium Option

              // Regular grouped radios
              // Special handling for validation rules example
              template(v-else-if="variation.id === 'validation-rules'")
                .mb-4
                  h5.mb-2 Required validation
                  .mb-2
                    small.text--secondary Selected: {{ JSON.stringify(rulesRadioValue) }}
                  yz-radio-group(ref="rulesRadioGroup" v-model="rulesRadioValue" required, requiredErrorMessage="Please select an option")
                    yz-radio(value="option1" label="Option 1")
                    yz-radio(value="option2" label="Option 2")
                  .mt-4
                    .d-flex.gap-2
                      yz-btn(label="Reset" size="sm" @click="resetRulesRadioGroup")
                      yz-btn(label="Validate" @click="validateRulesRadioGroup" size="sm" outlined)
                    span.yz-regular-body-xs.ml-2(v-if="rulesValidationResult" :class="{ 'success--text': rulesValidationResult === 'Valid', 'error--text': rulesValidationResult === 'Invalid' }") {{ rulesValidationResult }}
                
                .mb-4
                  h5.mb-2 Combined required + custom validation rules
                  .mb-2
                    small.text--secondary Selected: {{ JSON.stringify(rulesRadioValue2) }}
                  yz-radio-group(ref="customRulesRadioGroup" v-model="rulesRadioValue2" required, requiredErrorMessage="Please select an option" :rules="[v => v === 'option2' || 'Please select Option 2']")
                    yz-radio(value="option1" label="Option 1")
                    yz-radio(value="option2" label="Option 2")
                  .mt-4
                    .d-flex.gap-2
                      yz-btn(label="Reset" size="sm" @click="resetCustomRulesRadioGroup")
                      yz-btn(label="Validate" @click="validateCustomRulesRadioGroup" size="sm" outlined)
                    span.yz-regular-body-xs.ml-2(v-if="customRulesValidationResult" :class="{ 'success--text': customRulesValidationResult === 'Valid', 'error--text': customRulesValidationResult === 'Invalid' }") {{ customRulesValidationResult }}
                
                .mb-4
                  h5.mb-2 Required validation using only rules (no required prop)
                  .mb-2
                    small.text--secondary Selected: {{ JSON.stringify(rulesRadioValue3) }}
                  yz-radio-group(ref="rulesOnlyRadioGroup" v-model="rulesRadioValue3" :rules="[v => !!v || 'This field is required']")
                    yz-radio(value="option1" label="Option 1")
                    yz-radio(value="option2" label="Option 2")
                  .mt-4
                    .d-flex.gap-2
                      yz-btn(label="Reset" size="sm" @click="resetRulesOnlyRadioGroup")
                      yz-btn(label="Validate" @click="validateRulesOnlyRadioGroup" size="sm" outlined)
                    span.yz-regular-body-xs.ml-2(v-if="rulesOnlyValidationResult" :class="{ 'success--text': rulesOnlyValidationResult === 'Valid', 'error--text': rulesOnlyValidationResult === 'Invalid' }") {{ rulesOnlyValidationResult }}
              
              // Optional validation examples
              template(v-else-if="variation.id === 'optional-validation'")
                .mb-4
                  h5.mb-2 Notification Preferences (Optional)
                  .mb-2
                    small.text--secondary Selected: {{ JSON.stringify(optionalNotificationValue) }}
                  v-form(ref="optionalNotificationForm")
                    yz-radio-group(v-model="optionalNotificationValue" :rules="optionalNotificationRules")
                      yz-radio(value="email" label="Email notifications")
                      yz-radio(value="sms" label="SMS notifications")
                      yz-radio(value="push" label="Push notifications")
                    .mt-4
                      .d-flex.gap-2
                        yz-btn(label="Reset" size="sm" @click="resetOptionalNotificationForm")
                        yz-btn(label="Validate" @click="validateOptionalNotificationForm" size="sm" outlined)
                      span.yz-regular-body-xs.ml-2(v-if="optionalNotificationValidationResult" :class="{ 'success--text': optionalNotificationValidationResult === 'Valid', 'error--text': optionalNotificationValidationResult === 'Invalid' }") {{ optionalNotificationValidationResult }}
                
                .mb-4
                  h5.mb-2 Preferred Contact Method (Optional)
                  .mb-2
                    small.text--secondary Selected: {{ JSON.stringify(optionalContactValue) }}
                  v-form(ref="optionalContactForm")
                    yz-radio-group(v-model="optionalContactValue" :rules="optionalContactRules")
                      yz-radio(value="phone" label="Phone call")
                      yz-radio(value="email" label="Email")
                      yz-radio(value="text" label="Text message")
                    .mt-4
                      .d-flex.gap-2
                        yz-btn(label="Reset" size="sm" @click="resetOptionalContactForm")
                        yz-btn(label="Validate" @click="validateOptionalContactForm" size="sm" outlined)
                      span.yz-regular-body-xs.ml-2(v-if="optionalContactValidationResult" :class="{ 'success--text': optionalContactValidationResult === 'Valid', 'error--text': optionalContactValidationResult === 'Invalid' }") {{ optionalContactValidationResult }}
                
                .mb-4
                  h5.mb-2 Theme Preference (Optional)
                  .mb-2
                    small.text--secondary Selected: {{ JSON.stringify(optionalThemeValue) }}
                  v-form(ref="optionalThemeForm")
                    yz-radio-group(v-model="optionalThemeValue" :rules="optionalThemeRules")
                      yz-radio(value="light" label="Light theme")
                      yz-radio(value="dark" label="Dark theme")
                      yz-radio(value="auto" label="Auto (system preference)")
                    .mt-4
                      .d-flex.gap-2
                        yz-btn(label="Reset" size="sm" @click="resetOptionalThemeForm")
                        yz-btn(label="Validate" @click="validateOptionalThemeForm" size="sm" outlined)
                      span.yz-regular-body-xs.ml-2(v-if="optionalThemeValidationResult" :class="{ 'success--text': optionalThemeValidationResult === 'Valid', 'error--text': optionalThemeValidationResult === 'Invalid' }") {{ optionalThemeValidationResult }}
              
              // Regular grouped radios  
              template(v-else)
                .mb-3
                  strong Selected: {{ JSON.stringify($data[variation.radios[0].model]) }}
                yz-radio-group(v-model="$data[variation.radios[0].model]")
                  yz-radio(v-for="(radio, index) in variation.radios" :key="`${variation.id}-radio-item-${index}`" :value="radio.value" :label="radio.label" v-bind="radio.props")

            .variation-code
              .code-header.d-flex.align-center.mb-2
                span.font-weight-medium Code Examples

              // Special code examples for optional validation
              template(v-if="variation.id === 'optional-validation'")
                .code-examples
                  .mb-3
                    .d-flex.align-center.mb-1
                      yz-btn(icon-only size="md" :icon="copiedCode === optionalNotificationCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === optionalNotificationCode ? 'success' : ''" @click="copyCode(optionalNotificationCode)")
                      span.font-weight-medium.ml-2 Optional notification preference
                    pre.code-block.pa-2.rounded.text-caption.ml-md-8(style="background-color: #f5f5f5; overflow-x: auto;")
                      code {{ optionalNotificationCode }}
                  
                  .mb-3
                    .d-flex.align-center.mb-1
                      yz-btn(icon-only size="md" :icon="copiedCode === optionalContactCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === optionalContactCode ? 'success' : ''" @click="copyCode(optionalContactCode)")
                      span.font-weight-medium.ml-2 Optional contact method
                    pre.code-block.pa-2.rounded.text-caption.ml-md-8(style="background-color: #f5f5f5; overflow-x: auto;")
                      code {{ optionalContactCode }}
                  
                  .mb-3
                    .d-flex.align-center.mb-1
                      yz-btn(icon-only size="md" :icon="copiedCode === optionalThemeCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === optionalThemeCode ? 'success' : ''" @click="copyCode(optionalThemeCode)")
                      span.font-weight-medium.ml-2 Optional theme preference
                    pre.code-block.pa-2.rounded.text-caption.ml-md-8(style="background-color: #f5f5f5; overflow-x: auto;")
                      code {{ optionalThemeCode }}

              // Regular code examples
              template(v-else)
                .code-examples
                  div(v-for="(radio, index) in variation.radios" :key="`${variation.id}-code-${index}`" class="mb-3")
                    .d-flex.align-center.mb-1
                      yz-btn(icon-only size="md" :icon="copiedCode === radio.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === radio.code ? 'success' : ''" @click="copyCode(radio.code)")
                      span.font-weight-medium.ml-2 {{ radio.label }}
                    pre.code-block.pa-2.rounded.text-caption.ml-md-8(style="background-color: #f5f5f5; overflow-x: auto;")
                      code {{ radio.code }}

          // Individual radio
          template(v-else)
            .variation-preview.mb-4.pa-4.border.rounded
              yz-radio(v-model="variation.model" :radio-value="variation.value" :label="variation.label" v-bind="variation.props")

            .variation-code
              .code-header.d-flex.align-center.mb-2
                yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
                span.font-weight-medium.ml-2 Code

              pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
                code {{ variation.code }}


    // API Documentation
    .component-section.mb-8#api
      h3.mb-4.cursor-pointer(@click="navigateToSection('api')") API
      
      // YzRadioGroup Props
      h4.mb-3 YzRadioGroup Props
      .props-table.mb-6
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Prop
                th Type
                th Default
                th Description
            tbody
              tr(v-for="prop in radioGroupProps" :key="prop.name")
                td.font-weight-medium.text-no-wrap {{ prop.name }}
                td.text--secondary.text-no-wrap {{ prop.type }}
                td.text--secondary.text-no-wrap {{ prop.default }}
                td.single-line {{ prop.description }}

      // YzRadio Props  
      h4.mb-3 YzRadio Props
      .props-table
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Prop
                th Type
                th Default
                th Description
            tbody
              tr(v-for="prop in radioProps" :key="prop.name")
                td.font-weight-medium.text-no-wrap {{ prop.name }}
                td.text--secondary.text-no-wrap {{ prop.type }}
                td.text--secondary.text-no-wrap {{ prop.default }}
                td.single-line {{ prop.description }}

      // Slots Documentation
      h4.mb-3.mt-6 YzRadio Slots
      .props-table
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Slot Name
                th Description
            tbody
              tr(v-for="slot in radioSlots" :key="slot.name")
                td.font-weight-medium.text-no-wrap {{ slot.name }}
                td.single-line {{ slot.description }}
</template>

<script>
export default {
  data() {
    return {
      copiedCode: null,
      basicRadioValue: 'option1',
      disabledRadioValue: 'disabled-selected',
      invalidRadioValue: 'invalid-selected',
      gapRadioValue: 'gap-8',
      rulesRadioValue: null,
      typesRadioValue: 'string-value',
      slotRadioValue: 'text-label',
      rulesRadioValue2: null,
      rulesRadioValue3: null,
      rulesValidationResult: null,
      customRulesValidationResult: null,
      rulesOnlyValidationResult: null,
      layoutHorizontalDefaultValue: 'layout-a',
      layoutHorizontal32Value: 'layout-a',
      layoutVerticalDefaultValue: 'layout-a',
      layoutVertical16Value: 'layout-a',
      
      // Optional Validation Examples Data
      optionalNotificationValue: null,
      optionalContactValue: null,
      optionalThemeValue: null,
      optionalNotificationValidationResult: null,
      optionalContactValidationResult: null,
      optionalThemeValidationResult: null,
      radioVariations: [
        {
          name: 'Basic Radio',
          id: 'basic-radio',
          description: 'Standard radio button options',
          isGroup: true,
          radios: [
            {
              label: 'Option 1',
              value: 'option1',
              model: 'basicRadioValue',
              props: {},
              code: 'yz-radio-group(v-model="selectedValue")\n  yz-radio(value="option1" label="Option 1")'
            },
            {
              label: 'Option 2',
              value: 'option2',
              model: 'basicRadioValue',
              props: {},
              code: 'yz-radio-group(v-model="selectedValue")\n  yz-radio(value="option2" label="Option 2")'
            },
            {
              label: 'Option 3',
              value: 'option3',
              model: 'basicRadioValue',
              props: {},
              code: 'yz-radio-group(v-model="selectedValue")\n  yz-radio(value="option3" label="Option 3")'
            }
          ]
        },
        {
          name: 'Disabled States',
          id: 'disabled-states',
          description: 'Disabled radio button states',
          isGroup: true,
          radios: [
            {
              label: 'Enabled',
              value: 'enabled',
              model: 'disabledRadioValue',
              props: {},
              code: 'yz-radio-group(v-model="selectedValue")\n  yz-radio(value="enabled" label="Enabled")'
            },
            {
              label: 'Disabled',
              value: 'disabled',
              model: 'disabledRadioValue',
              props: { disabled: true },
              code: 'yz-radio-group(v-model="selectedValue")\n  yz-radio(value="disabled" label="Disabled" disabled)'
            },
            {
              label: 'Disabled & Selected',
              value: 'disabled-selected',
              model: 'disabledRadioValue',
              props: { disabled: true },
              code: 'yz-radio-group(v-model="selectedValue")\n  yz-radio(value="disabled-selected" label="Disabled & Selected" disabled)'
            }
          ]
        },
        {
          name: 'Invalid States',
          id: 'invalid-states',
          description: 'Invalid radio button states',
          isGroup: true,
          radios: [
            {
              label: 'Valid',
              value: 'valid',
              model: 'invalidRadioValue',
              props: {},
              code: 'yz-radio-group(v-model="selectedValue")\n  yz-radio(value="valid" label="Valid")'
            },
            {
              label: 'Invalid',
              value: 'invalid',
              model: 'invalidRadioValue',
              props: { invalid: true },
              code: 'yz-radio-group(v-model="selectedValue")\n  yz-radio(value="invalid" label="Invalid" invalid)'
            },
            {
              label: 'Invalid & Selected',
              value: 'invalid-selected',
              model: 'invalidRadioValue',
              props: { invalid: true },
              code: 'yz-radio-group(v-model="selectedValue")\n  yz-radio(value="invalid-selected" label="Invalid & Selected" invalid)'
            }
          ]
        },
        {
          name: 'Validation with Rules',
          id: 'validation-rules',
          description: 'Radio group validation using rules array and v-form integration',
          isGroup: true,
          radios: [
            {
              label: 'Required validation (rules)',
              value: 'rules-required',
              model: 'rulesRadioValue',
              props: {},
              code: 'yz-radio-group(v-model="selectedValue" required required-error-message="Please select an option")\n  yz-radio(value="option1" label="Option 1")\n  yz-radio(value="option2" label="Option 2")'
            },
            {
              label: 'Combined required + custom validation rules',
              value: 'rules-custom',
              model: 'rulesRadioValue',
              props: {},
              code: 'yz-radio-group(v-model="selectedValue" required required-error-message="Please select an option" :rules="[v => v === \'option2\' || \'Please select Option 2\']")\n  yz-radio(value="option1" label="Option 1")\n  yz-radio(value="option2" label="Option 2")'
            },
            {
              label: 'Required validation using only rules',
              value: 'rules-only',
              model: 'rulesRadioValue3',
              props: {},
              code: 'yz-radio-group(v-model="selectedValue" :rules="[v => !!v || \'This field is required\']")\n  yz-radio(value="option1" label="Option 1")\n  yz-radio(value="option2" label="Option 2")'
            }
          ]
        },
        {
          name: 'Optional Validation',
          id: 'optional-validation',
          description: 'Optional fields that validate only when a selection is made - useful for user preferences and optional settings',
          isGroup: true,
          radios: []
        },
        {
          name: 'Label Gap Variations',
          id: 'label-gap-variations',
          description: 'Different label gap spacing options',
          isGroup: true,
          radios: [
            {
              label: '4px Gap',
              value: 'gap-4',
              model: 'gapRadioValue',
              props: { labelGap: '4px' },
              code: 'yz-radio-group(v-model="selectedValue")\n  yz-radio(value="gap-4" label="4px Gap" label-gap="4px")'
            },
            {
              label: '8px Gap (default)',
              value: 'gap-8',
              model: 'gapRadioValue',
              props: { labelGap: '8px' },
              code: 'yz-radio-group(v-model="selectedValue")\n  yz-radio(value="gap-8" label="8px Gap (default)" label-gap="8px")'
            },
            {
              label: '12px Gap',
              value: 'gap-12',
              model: 'gapRadioValue',
              props: { labelGap: '12px' },
              code: 'yz-radio-group(v-model="selectedValue")\n  yz-radio(value="gap-12" label="12px Gap" label-gap="12px")'
            },
            {
              label: '16px Gap',
              value: 'gap-16',
              model: 'gapRadioValue',
              props: { labelGap: '16px' },
              code: 'yz-radio-group(v-model="selectedValue")\n  yz-radio(value="gap-16" label="16px Gap" label-gap="16px")'
            }
          ]
        },
        {
          name: 'Different Value Types',
          id: 'different-value-types',
          description: 'Radio buttons with boolean, number, and object values',
          isGroup: true,
          radios: [
            {
              label: 'String Value',
              value: 'string-value',
              model: 'typesRadioValue',
              props: {},
              code: 'yz-radio-group(v-model="selectedValue")\n  yz-radio(value="string-value" label="String Value")'
            },
            {
              label: 'Boolean True',
              value: true,
              model: 'typesRadioValue',
              props: {},
              code: 'yz-radio-group(v-model="selectedValue")\n  yz-radio(:value="true" label="Boolean True")'
            },
            {
              label: 'Boolean False',
              value: false,
              model: 'typesRadioValue',
              props: {},
              code: 'yz-radio-group(v-model="selectedValue")\n  yz-radio(:value="false" label="Boolean False")'
            },
            {
              label: 'Number 123',
              value: 123,
              model: 'typesRadioValue',
              props: {},
              code: 'yz-radio-group(v-model="selectedValue")\n  yz-radio(:value="123" label="Number 123")'
            },
            {
              label: 'Object Value',
              value: { id: 1, name: 'Object' },
              model: 'typesRadioValue',
              props: {},
              code: 'yz-radio-group(v-model="selectedValue")\n  yz-radio(:value="{ id: 1, name: \'Object\' }" label="Object Value")'
            }
          ]
        },
        {
          name: 'Label Slot',
          id: 'label-slot',
          description: 'Using custom content in the label slot',
          isGroup: true,
          radios: [
            {
              label: 'Text Label',
              value: 'text-label',
              model: 'slotRadioValue',
              props: {},
              code: 'yz-radio-group(v-model="selectedValue")\n  yz-radio(value="text-label" label="Text Label")'
            },
            {
              label: 'Slot Label',
              value: 'slot-label',
              model: 'slotRadioValue',
              props: {},
              code: 'yz-radio-group(v-model="selectedValue")\n  yz-radio(value="slot-label")\n    template(#label)\n      span(v-html="\'Custom slot with <strong class="text-danger-500">HTML content</strong>\'")',
              isSlot: true
            },
            {
              label: 'Rich Content',
              value: 'rich-content',
              model: 'slotRadioValue',
              props: {},
              code: 'yz-radio-group(v-model="selectedValue")\n  yz-radio(value="rich-content")\n    template(#label)\n      .d-flex.align-center\n        v-icon.mr-2 mdi-star\n        span Premium Option',
              isSlot: true,
              isRich: true
            }
          ]
        },
        {
          name: 'Layout Variations',
          id: 'layout-variations',
          description: 'Different layout orientations and custom spacing',
          isGroup: true,
          radios: [
            {
              label: 'Vertical Layout (default)',
              value: 'vertical-default',
              model: 'layoutVerticalDefaultValue',
              props: {},
              code: 'yz-radio-group(v-model="selectedValue")\n  yz-radio(value="layout-a" label="Layout A")\n  yz-radio(value="layout-b" label="Layout B")\n  yz-radio(value="layout-c" label="Layout C")',
              layoutType: 'vertical-default'
            },
            {
              label: 'Vertical with 32px spacing',
              value: 'vertical-32',
              model: 'layoutVertical16Value',
              props: {},
              code: 'yz-radio-group(v-model="selectedValue" spacing="32px")\n  yz-radio(value="layout-a" label="Layout A")\n  yz-radio(value="layout-b" label="Layout B")\n  yz-radio(value="layout-c" label="Layout C")',
              layoutType: 'vertical-32'
            },
            {
              label: 'Horizontal Layout',
              value: 'horizontal-default',
              model: 'layoutHorizontalDefaultValue',
              props: {},
              code: 'yz-radio-group(v-model="selectedValue" direction="horizontal")\n  yz-radio(value="layout-a" label="Layout A")\n  yz-radio(value="layout-b" label="Layout B")\n  yz-radio(value="layout-c" label="Layout C")',
              layoutType: 'horizontal-default'
            },
            {
              label: 'Horizontal with 32px spacing',
              value: 'horizontal-32',
              model: 'layoutHorizontal32Value',
              props: {},
              code: 'yz-radio-group(v-model="selectedValue" direction="horizontal" spacing="32px")\n  yz-radio(value="layout-a" label="Layout A")\n  yz-radio(value="layout-b" label="Layout B")\n  yz-radio(value="layout-c" label="Layout C")',
              layoutType: 'horizontal-32'
            }
          ]
        }
      ],
      radioGroupProps: [
        {
          name: 'v-model',
          type: 'Any',
          default: 'null',
          description: 'The selected value for the radio group'
        },
        {
          name: 'direction',
          type: 'String',
          default: 'vertical',
          description: 'Layout direction: "vertical" or "horizontal"'
        },
        {
          name: 'spacing',
          type: 'String|Number',
          default: '20',
          description: 'Spacing between radio buttons (in px)'
        },
        {
          name: 'required',
          type: 'Boolean',
          default: 'false',
          description: 'Whether the radio group requires a selection'
        },
        {
          name: 'requiredErrorMessage',
          type: 'String',
          default: 'This field is required',
          description: 'Custom error message to display when required validation fails'
        },
        {
          name: 'rules',
          type: 'Array',
          default: '[]',
          description: 'Array of validation rules. Each rule is a function that receives the value and returns true if valid, or an error message if invalid'
        }
      ],
      radioProps: [
        {
          name: 'value',
          type: 'Any',
          default: '-',
          description: 'The value of this radio option (required)'
        },
        {
          name: 'label',
          type: 'String',
          default: '',
          description: 'Radio button label text'
        },
        {
          name: 'disabled',
          type: 'Boolean',
          default: 'false',
          description: 'Whether the radio is disabled'
        },
        {
          name: 'invalid',
          type: 'Boolean',
          default: 'false',
          description: 'Whether the radio is in invalid state'
        },
        {
          name: 'labelGap',
          type: 'String',
          default: '8px',
          description: 'Gap between radio button and label'
        },
        {
          name: 'labelClass',
          type: 'String',
          default: 'yz-regular-body-sm',
          description: 'CSS class applied to the label text'
        }
      ],
      radioSlots: [
        {
          name: 'label',
          description: 'Custom content for the radio button label'
        }
      ],
      radioGroupMethods: [
        {
          name: 'validate(force)',
          returns: 'Boolean',
          description: 'Manually triggers validation and returns validation result (true = valid, false = invalid). Pass force=true to show validation errors immediately'
        },
        {
          name: 'resetValidation()',
          returns: 'void',
          description: 'Clears validation errors without resetting the value'
        }
      ]
    }
  },
  computed: {
    // Optional Validation Rules
    optionalNotificationRules() {
      return [
        v => !v || ['email', 'sms', 'push'].includes(v) || 'Please select a valid notification preference'
      ]
    },
    optionalContactRules() {
      return [
        v => !v || ['email', 'phone', 'mail'].includes(v) || 'Please select a valid contact method'
      ]
    },
    optionalThemeRules() {
      return [
        v => !v || ['light', 'dark', 'system'].includes(v) || 'Please select a valid theme preference'
      ]
    },
    
    // Code examples for optional validation
    optionalNotificationCode() {
      return `yz-radio-group(v-model="notificationPreference" :rules="optionalNotificationRules")
  yz-radio(value="email" label="Email notifications")
  yz-radio(value="sms" label="SMS notifications") 
  yz-radio(value="push" label="Push notifications")

// Validation rule
optionalNotificationRules() {
  return [
    v => !v || ['email', 'sms', 'push'].includes(v) || 'Please select a valid notification preference'
  ]
}`
    },
    optionalContactCode() {
      return `yz-radio-group(v-model="contactMethod" :rules="optionalContactRules")
  yz-radio(value="email" label="Email contact")
  yz-radio(value="phone" label="Phone contact")
  yz-radio(value="mail" label="Physical mail")

// Validation rule
optionalContactRules() {
  return [
    v => !v || ['email', 'phone', 'mail'].includes(v) || 'Please select a valid contact method'
  ]
}`
    },
    optionalThemeCode() {
      return `yz-radio-group(v-model="themePreference" :rules="optionalThemeRules")
  yz-radio(value="light" label="Light theme")
  yz-radio(value="dark" label="Dark theme")
  yz-radio(value="system" label="System preference")

// Validation rule
optionalThemeRules() {
  return [
    v => !v || ['light', 'dark', 'system'].includes(v) || 'Please select a valid theme preference'
  ]
}`
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.scrollToSection()
    })
  },
  watch: {
    '$route.hash': {
      handler() {
        this.scrollToSection()
      },
      immediate: false
    }
  },
  methods: {
    navigateToSection(sectionId) {
      // Update the route with the section hash
      this.$router.push({
        name: this.$route.name,
        hash: `#${sectionId}`,
        query: this.$route.query
      })
    },
    validateRulesRadioGroup() {
      this.$nextTick(() => {
        const radioGroupRefArray = this.$refs.rulesRadioGroup;
        const radioGroupRef = Array.isArray(radioGroupRefArray) ? radioGroupRefArray[0] : radioGroupRefArray;
        if (radioGroupRef && typeof radioGroupRef.validate === 'function') {
          const isValid = radioGroupRef.validate(true);
          this.rulesValidationResult = isValid ? 'Valid' : 'Invalid';
          setTimeout(() => {
            this.rulesValidationResult = null;
          }, 1500);
        }
      });
    },
    validateCustomRulesRadioGroup() {
      this.$nextTick(() => {
        const radioGroupRefArray = this.$refs.customRulesRadioGroup;
        const radioGroupRef = Array.isArray(radioGroupRefArray) ? radioGroupRefArray[0] : radioGroupRefArray;
        if (radioGroupRef && typeof radioGroupRef.validate === 'function') {
          const isValid = radioGroupRef.validate(true);
          this.customRulesValidationResult = isValid ? 'Valid' : 'Invalid';
          setTimeout(() => {
            this.customRulesValidationResult = null;
          }, 1500);
        }
      });
    },
    validateRulesOnlyRadioGroup() {
      this.$nextTick(() => {
        const radioGroupRefArray = this.$refs.rulesOnlyRadioGroup;
        const radioGroupRef = Array.isArray(radioGroupRefArray) ? radioGroupRefArray[0] : radioGroupRefArray;
        if (radioGroupRef && typeof radioGroupRef.validate === 'function') {
          const isValid = radioGroupRef.validate(true);
          this.rulesOnlyValidationResult = isValid ? 'Valid' : 'Invalid';
          setTimeout(() => {
            this.rulesOnlyValidationResult = null;
          }, 1500);
        }
      });
    },
    resetRulesRadioGroup() {
      this.$nextTick(() => {
        const radioGroupRefArray = this.$refs.rulesRadioGroup;
        const radioGroupRef = Array.isArray(radioGroupRefArray) ? radioGroupRefArray[0] : radioGroupRefArray;
        if (radioGroupRef && typeof radioGroupRef.resetValidation === 'function') {
          radioGroupRef.resetValidation();
          this.rulesValidationResult = null;
          this.rulesRadioValue = null;
        }
      });
    },
    resetCustomRulesRadioGroup() {
      this.$nextTick(() => {
        const radioGroupRefArray = this.$refs.customRulesRadioGroup;
        const radioGroupRef = Array.isArray(radioGroupRefArray) ? radioGroupRefArray[0] : radioGroupRefArray;
        if (radioGroupRef && typeof radioGroupRef.resetValidation === 'function') {
          radioGroupRef.resetValidation();
          this.customRulesValidationResult = null;
          this.rulesRadioValue2 = null;
        }
      });
    },
    resetRulesOnlyRadioGroup() {
      this.$nextTick(() => {
        const radioGroupRefArray = this.$refs.rulesOnlyRadioGroup;
        const radioGroupRef = Array.isArray(radioGroupRefArray) ? radioGroupRefArray[0] : radioGroupRefArray;
        if (radioGroupRef && typeof radioGroupRef.resetValidation === 'function') {
          radioGroupRef.resetValidation();
          this.rulesOnlyValidationResult = null;
          this.rulesRadioValue3 = null;
        }
      });
    },
    
    // Optional Validation Methods
    validateOptionalNotificationForm() {
      this.$nextTick(() => {
        const formRefArray = this.$refs.optionalNotificationForm;
        const formRef = Array.isArray(formRefArray) ? formRefArray[0] : formRefArray;
        if (formRef && typeof formRef.validate === 'function') {
          const isValid = formRef.validate();
          this.optionalNotificationValidationResult = isValid ? 'Valid' : 'Invalid';
          setTimeout(() => {
            this.optionalNotificationValidationResult = null;
          }, 1500);
        }
      });
    },
    resetOptionalNotificationForm() {
      this.$nextTick(() => {
        const formRefArray = this.$refs.optionalNotificationForm;
        const formRef = Array.isArray(formRefArray) ? formRefArray[0] : formRefArray;
        if (formRef && typeof formRef.resetValidation === 'function') {
          formRef.resetValidation();
          this.optionalNotificationValidationResult = null;
          this.optionalNotificationValue = null;
        }
      });
    },
    validateOptionalContactForm() {
      this.$nextTick(() => {
        const formRefArray = this.$refs.optionalContactForm;
        const formRef = Array.isArray(formRefArray) ? formRefArray[0] : formRefArray;
        if (formRef && typeof formRef.validate === 'function') {
          const isValid = formRef.validate();
          this.optionalContactValidationResult = isValid ? 'Valid' : 'Invalid';
          setTimeout(() => {
            this.optionalContactValidationResult = null;
          }, 1500);
        }
      });
    },
    resetOptionalContactForm() {
      this.$nextTick(() => {
        const formRefArray = this.$refs.optionalContactForm;
        const formRef = Array.isArray(formRefArray) ? formRefArray[0] : formRefArray;
        if (formRef && typeof formRef.resetValidation === 'function') {
          formRef.resetValidation();
          this.optionalContactValidationResult = null;
          this.optionalContactValue = null;
        }
      });
    },
    validateOptionalThemeForm() {
      this.$nextTick(() => {
        const formRefArray = this.$refs.optionalThemeForm;
        const formRef = Array.isArray(formRefArray) ? formRefArray[0] : formRefArray;
        if (formRef && typeof formRef.validate === 'function') {
          const isValid = formRef.validate();
          this.optionalThemeValidationResult = isValid ? 'Valid' : 'Invalid';
          setTimeout(() => {
            this.optionalThemeValidationResult = null;
          }, 1500);
        }
      });
    },
    resetOptionalThemeForm() {
      this.$nextTick(() => {
        const formRefArray = this.$refs.optionalThemeForm;
        const formRef = Array.isArray(formRefArray) ? formRefArray[0] : formRefArray;
        if (formRef && typeof formRef.resetValidation === 'function') {
          formRef.resetValidation();
          this.optionalThemeValidationResult = null;
          this.optionalThemeValue = null;
        }
      });
    },
    
    scrollToSection() {
      const sectionIdWithHash = this.$route.hash
      const sectionId = sectionIdWithHash.replace('#', '')
      if (sectionId) {
        this.$nextTick(() => {
          const element = document.getElementById(sectionId)
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            })
          }
        })
      }
    },
    async copyCode(code) {
      try {
        await navigator.clipboard.writeText(code)
        this.copiedCode = code
        setTimeout(() => {
          this.copiedCode = null
        }, 2000)
      } catch (err) {
        console.error('Failed to copy code:', err)
      }
    }
  }
}
</script>

<style scoped>
.variations-grid {
  max-width: 100%;
}

.variation-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  background-color: #ffffff;
}

.variation-preview {
  background-color: #fafafa;
  border: 1px dashed #d0d0d0 !important;
}

.code-block {
  background-color: #f8f9fa !important;
  border: 1px solid #e9ecef;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  line-height: 1.5;
}

.code-header {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 8px;
}

.component-section {
  background-color: #ffffff;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  padding: 24px;
}

.props-table .v-data-table {
  border: 1px solid #e0e0e0;
}

.single-line {
  white-space: normal;
  word-wrap: break-word;
  max-width: 400px;
}

.text-no-wrap {
  white-space: nowrap;
}

.cursor-pointer {
  cursor: pointer;
  transition: color 0.2s ease;
}

.cursor-pointer:hover {
  color: #1976d2;
}

.gap-3 > *:not(:last-child) {
  margin-bottom: 0.75rem;
}
</style>